import React from 'react'
import { Box, Card, SrOnly, Text, Divider, getPaletteColor } from 'pcln-design-system'
import { Pin as PinIcon, Gps as GpsIcon } from 'pcln-icons'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import DropdownItem from './DropdownItem'
import NonSelectableDropdownItem from './NonSelectableDropdownItem'
import { ICON_MAP } from './constants'

const DropdownCard = styled(Card)`
  border-color: transparent;
  &:focus {
    outline: none;
  }
  display: ${(props) => (props.visible ? 'block' : 'none')};
  position: absolute;
  z-index: 2;
  ${({ minWidth }) =>
    minWidth &&
    `
    min-width: ${minWidth};
  `}
`

const focusAndHoverStyles = css`
  background-color: ${getPaletteColor('primary.base')};
  color: ${getPaletteColor('text.lightest')};

  svg {
    color: ${getPaletteColor('text.lightest')};
  }
`
const StyledDropdownItem = styled(DropdownItem)`
  background-color: ${(props) => (props.isHighlighted ? getPaletteColor('primary.base') : null)};
  color: ${(props) => (props.isHighlighted ? getPaletteColor('text.lightest') : null)};
  svg {
    color: ${(props) => (props.isHighlighted ? getPaletteColor('text.lightest') : null)};
  }

  ${(props) =>
    !props.isMobile &&
    css`
      &:hover {
        ${focusAndHoverStyles};
      }
    `} &:focus {
    ${focusAndHoverStyles};
  }
`

const StyledDropdownItemDisabled = styled(DropdownItem)`
  background-color: ${(props) => (props.isHighlighted ? null : getPaletteColor('text.lightest'))};
  color: ${(props) => (props.isHighlighted ? null : getPaletteColor('text.light'))};
  svg {
    color: ${(props) => (props.isHighlighted ? null : getPaletteColor('text.light'))};
  }
  cursor: default;

  &:hover,
  &:focus {
    background-color: ${getPaletteColor('background.lightest')};
    color: ${getPaletteColor('text.light')};
    svg {
      color: ${getPaletteColor('text.light')};
    }
  }
`

const TypeaheadItemPropType = PropTypes.shape({
  type: PropTypes.string.isRequired,
  content: PropTypes.string,
  displayName: PropTypes.string,
  isNearby: PropTypes.bool,
  isRecent: PropTypes.bool,
  isPopularDestination: PropTypes.bool,
})

const propTypes = {
  id: PropTypes.string.isRequired,
  showDropdown: PropTypes.bool,
  searchItems: PropTypes.arrayOf(TypeaheadItemPropType),
  nearbyHeading: PropTypes.string,
  nearbyItems: PropTypes.arrayOf(TypeaheadItemPropType),
  recentHeading: PropTypes.string,
  isMobile: PropTypes.bool,
  recentItems: PropTypes.arrayOf(TypeaheadItemPropType),
  onClick: PropTypes.func.isRequired,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  highlightedIndex: PropTypes.number,
  showCurrentLocation: PropTypes.bool,
  currentLocation: PropTypes.shape({
    type: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }),
  isCurrentLocationDisabled: PropTypes.bool,
  isCurrentLocationTimedOut: PropTypes.bool,
  popularDestHeading: PropTypes.string,
  popularDestItems: PropTypes.arrayOf(TypeaheadItemPropType),
  dropdownItemIconProps: PropTypes.object,
  dropdownItemTextProps: PropTypes.object,
  minWidth: PropTypes.string,
  iconMap: PropTypes.object,
}

const defaultProps = {
  recentHeading: 'Recent Searches',
  nearbyHeading: 'Nearby',
  popularDestHeading: 'Popular Destinations',
  showCurrentLocation: false,
  isCurrentLocationDisabled: false,
  isCurrentLocationTimedOut: false,
  width: 1,
  iconMap: ICON_MAP,
}

class Dropdown extends React.Component {
  constructor() {
    super()
    this.renderSearchItem = this.renderSearchItem.bind(this)
    this.renderAllSearchItems = this.renderAllSearchItems.bind(this)
    this.dropdownCardRef = React.createRef()
  }

  componentDidUpdate() {
    this.handleAutoScroll()
  }

  handleAutoScroll() {
    if (this.highlightedItemElem && this.dropdownCardRef?.current) {
      const dropdownCardElm = this.dropdownCardRef?.current
      const dropdownRect = dropdownCardElm.getBoundingClientRect()
      const highlightedItemRect = this.highlightedItemElem.getBoundingClientRect()
      const highlightedOffset = this.highlightedItemElem.offsetTop
      if (highlightedItemRect.top < dropdownRect.top) {
        dropdownCardElm.scrollTop = highlightedOffset
      } else if (highlightedItemRect.bottom > dropdownRect.bottom) {
        dropdownCardElm.scrollTop = highlightedOffset + highlightedItemRect.height - dropdownRect.height
      }
    }
  }

  renderAllSearchItems(searchItems) {
    let nonSelectableCount = 0
    return searchItems.map((item, index) => {
      if (item.type === 'NON_SELECTABLE_CITY') {
        nonSelectableCount++
        return (
          <NonSelectableDropdownItem
            displayName={item.displayName || item.content || item.itemName}
            Icon={this.props.iconMap[item.type] || PinIcon}
          />
        )
      }
      return this.renderSearchItem(item, index - nonSelectableCount)
    })
  }

  renderSearchItem(item, index) {
    const iconName = item.type

    const Icon = this.props.iconMap[item.type] || PinIcon

    const key = `${this.props.id}-item-${index}`
    return (
      <StyledDropdownItem
        key={key}
        data-autobot-element-id={key}
        data-testid={key}
        id={key}
        item={item}
        isMobile={this.props.isMobile}
        onClick={this.props.onClick}
        isHighlighted={index === this.props.highlightedIndex}
      >
        <Icon color='primary' mx={2} title={iconName} size={24} {...this.props.dropdownItemIconProps} />
        <div
          ref={(elem) => {
            if (index === this.props.highlightedIndex) {
              this.highlightedItemElem = elem
            }
          }}
        >
          <Text
            py='12px'
            mr='8px'
            aria-selected={index === this.props.highlightedIndex}
            data-autobot-element-id='typeahead-dropdown-item'
            data-testid='typeahead-dropdown-item'
            fontSize={0}
            {...this.props.dropdownItemTextProps}
          >
            {item.displayName || item.content || item.itemName}
          </Text>
        </div>
      </StyledDropdownItem>
    )
  }

  renderCurrentLocation(location = {}, isDisabled, isTimedout) {
    if (isDisabled) {
      return (
        <StyledDropdownItemDisabled
          id={`${this.props.id}-cur-loc`}
          item={location}
          onClick={this.props.onClick}
        >
          <GpsIcon color='text.light' mx={2} size={24} />
          <Text py={12} mr={2} fontSize={0}>
            {location.content}
          </Text>
        </StyledDropdownItemDisabled>
      )
    } else if (isTimedout) {
      return (
        <DropdownItem id={`${this.props.id}-cur-loc`} item={location} onClick={this.props.onClick}>
          <GpsIcon color='primary' mx={2} size={24} />
          <Text py={12} mr={2} fontSize={0}>
            {location.content}
          </Text>
        </DropdownItem>
      )
    } else {
      return (
        <StyledDropdownItem id={`${this.props.id}-cur-loc`} item={location} onClick={this.props.onClick}>
          <GpsIcon color='primary' mx={2} size={24} />
          <Text py={12} mr={2} fontSize={0}>
            {location.content}
          </Text>
        </StyledDropdownItem>
      )
    }
  }

  render() {
    const {
      searchItems,
      nearbyItems,
      recentItems,
      showDropdown,
      width,
      minWidth,
      showCurrentLocation,
      isCurrentLocationDisabled,
      isCurrentLocationTimedOut,
      currentLocation,
      popularDestItems,
    } = this.props

    return (
      <React.Fragment>
        {searchItems && searchItems.length > 0 && (
          <SrOnly aria-live='polite'>{searchItems.length} number of results</SrOnly>
        )}
        <DropdownCard
          boxShadowSize='lg'
          bg='white'
          borderRadius='xl'
          mt={1}
          id={this.props.id}
          data-testid='dropdown-card'
          width={width}
          minWidth={minWidth}
          visible={
            showDropdown &&
            ((searchItems && searchItems.length) ||
              (nearbyItems && nearbyItems.length) ||
              (recentItems && recentItems.length) ||
              (popularDestItems && popularDestItems.length) ||
              showCurrentLocation)
          }
        >
          <div
            ref={this.dropdownCardRef}
            style={{
              maxHeight: '340px',
              overflowY: 'auto',
            }}
          >
            <Box py={2} px={0} m={0} tabindex='0' role='listbox' id='typeahead-list'>
              {showCurrentLocation &&
                this.renderCurrentLocation(
                  currentLocation,
                  isCurrentLocationDisabled,
                  isCurrentLocationTimedOut
                )}

              {searchItems && this.renderAllSearchItems(searchItems)}

              {(!searchItems || searchItems.length === 0) && popularDestItems && popularDestItems.length > 0 && (
                <div>
                  <Text fontSize={0} p={2} bold fontWeight='bold'>
                    {this.props.popularDestHeading}
                  </Text>
                  <Divider my={0} />
                  <Box pb={2}>{popularDestItems.map(this.renderSearchItem)}</Box>
                </div>
              )}

              {(!searchItems || searchItems.length === 0) && recentItems && recentItems.length > 0 && (
                <div>
                  <Text fontSize={0} p={2} bold fontWeight='bold'>
                    {this.props.recentHeading}
                  </Text>
                  <Divider my={0} />
                  <Box pb={2}>{recentItems.map(this.renderSearchItem)}</Box>
                </div>
              )}

              {(!searchItems || searchItems.length === 0) && nearbyItems && nearbyItems.length > 0 && (
                <div>
                  <Text fontSize={0} p={2} bold fontWeight='bold'>
                    {this.props.nearbyHeading}
                  </Text>
                  <Divider my={0} />
                  <Box pb={2}>
                    {nearbyItems.map((item, index) =>
                      this.renderSearchItem(
                        item,
                        // If we have recentItems, add recentItems length to the index
                        // to be sure we can go up/down through both with the keyboard
                        recentItems && recentItems.length ? index + recentItems.length : index
                      )
                    )}
                  </Box>
                </div>
              )}
            </Box>
          </div>
        </DropdownCard>
      </React.Fragment>
    )
  }
}

Dropdown.propTypes = propTypes
Dropdown.defaultProps = defaultProps

export default Dropdown
